<!--
  A comment textarea that offers @-mentions.

  Typing `@` (at the start of a word) opens a list of everyone who may be
  mentioned on this document -- people with access and their agents, from
  `GET /v1/docs/:id/comment-targets` -- filtered as the person types
  (lib/mentions.ts). Picking one writes `@Name ` into the text and records the
  pick; the page sends the picks that are still in the text as `mentions`.

  THE COMBOBOX (ARIA 1.2, "combobox with listbox popup", editable). The
  textarea is the combobox and KEEPS FOCUS throughout: `aria-activedescendant`
  names the active option, `aria-expanded` says whether the list is open. The
  keys are listbox.ts's `openKey`, the share dialog's tested reducer, restricted
  to the ones that do not also mean something in a text field -- Up, Down,
  Enter, Escape and Tab reach the list; Space, Home and End stay the
  textarea's, because a name has spaces in it and Home is "start of line". A
  pick is announced in a polite status line.

  Escape closes the list for THIS `@`; typing a new one opens it again.
-->
<script lang="ts">
  import { tick } from 'svelte'
  import { openKey } from './listbox'
  import { filterCandidates, insertMention, mentionQuery, type MentionQuery } from '../mentions'
  import type { MentionCandidate, MentionRef } from '../types'

  let {
    id,
    label,
    value = $bindable(''),
    picked = $bindable<MentionRef[]>([]),
    candidates,
    placeholder = '',
    rows = 3,
    onsubmit,
    element = $bindable<HTMLTextAreaElement | null>(null),
  }: {
    id: string
    label: string
    value?: string
    picked?: MentionRef[]
    candidates: MentionCandidate[]
    placeholder?: string
    rows?: number
    /** Cmd/Ctrl+Enter. */
    onsubmit?: () => void
    element?: HTMLTextAreaElement | null
  } = $props()

  let query = $state<MentionQuery | null>(null)
  let dismissed = $state<number | null>(null)
  let active = $state(0)
  let said = $state('')

  const listId = $derived(`${id}-mentions`)
  const options = $derived(query ? filterCandidates(candidates, query.query) : [])
  const open = $derived(options.length > 0)

  /*
   * The secondary line. An agent's NAME is what the viewers strip calls it
   * ("Claude"), so whose agent it is goes here -- that is what keeps two
   * people's agents apart in the list. Its token label follows for its owner
   * only, who alone is sent one.
   */
  function describe(c: MentionCandidate): string {
    if (c.kind === 'agent') {
      const whose = c.forName ? `agent, for ${c.forName}` : 'agent'
      return c.detail ? `${whose} · ${c.detail}` : whose
    }
    return c.pending ? 'invited, not signed in yet' : 'person'
  }

  function reread() {
    const el = element
    if (!el || candidates.length === 0) {
      query = null
      return
    }
    const next = el.selectionStart === el.selectionEnd ? mentionQuery(el.value, el.selectionStart) : null
    if (next && next.start === dismissed) {
      query = null
      return
    }
    if (!next) dismissed = null
    if (next?.start !== query?.start) active = 0
    query = next
    if (active >= options.length) active = 0
  }

  async function pick(c: MentionCandidate) {
    const el = element
    if (!el || !query) return
    const out = insertMention(value, query.start, el.selectionStart, c.name)
    value = out.value
    picked = [...picked, { kind: c.kind, id: c.id, name: c.name, ...(c.forName ? { forName: c.forName } : {}) }]
    query = null
    said = `Mentioned ${c.forName ? `${c.name}, for ${c.forName}` : c.name}.`
    await tick()
    el.focus()
    el.setSelectionRange(out.caret, out.caret)
  }

  function onkeydown(e: KeyboardEvent) {
    if (open && ['ArrowDown', 'ArrowUp', 'Enter', 'Escape', 'Tab'].includes(e.key) && !e.metaKey && !e.ctrlKey) {
      const { action, prevent } = openKey(e.key, active, options.length)
      if (prevent) e.preventDefault()
      if (action.kind === 'move') active = action.active
      else if (action.kind === 'choose') void pick(options[action.index])
      else if (action.kind === 'close') {
        dismissed = query?.start ?? null
        query = null
      }
      return
    }
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      onsubmit?.()
    }
  }

  let listEl = $state<HTMLUListElement | null>(null)

  /*
   * The pointer's way to pick. Listened for directly rather than through an
   * `onclick` attribute: the list never takes focus -- the textarea keeps it,
   * and the keyboard reaches the options through `aria-activedescendant` -- so
   * there is no key handler on the list for the a11y lint to find, and adding
   * a dead one to satisfy it would be a lie. mousedown is swallowed so the
   * textarea does not lose focus (and close the list) before the click lands.
   */
  $effect(() => {
    const el = listEl
    if (!el) return
    const down = (e: MouseEvent) => e.preventDefault()
    const click = (e: MouseEvent) => {
      const li = (e.target as Element | null)?.closest('[data-index]')
      if (!li) return
      const c = options[Number(li.getAttribute('data-index'))]
      if (c) void pick(c)
    }
    el.addEventListener('mousedown', down)
    el.addEventListener('click', click)
    return () => {
      el.removeEventListener('mousedown', down)
      el.removeEventListener('click', click)
    }
  })
</script>

<div class="mention-field">
  <label class="visually-hidden" for={id}>{label}</label>
  <textarea
    {id}
    {rows}
    {placeholder}
    role="combobox"
    aria-autocomplete="list"
    aria-expanded={open}
    aria-controls={listId}
    aria-activedescendant={open ? `${listId}-${active}` : undefined}
    bind:value
    bind:this={element}
    oninput={reread}
    onclick={reread}
    onkeyup={(e) => {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'Home' || e.key === 'End') reread()
    }}
    onblur={() => (query = null)}
    {onkeydown}
  ></textarea>
  <!-- Always in the DOM so aria-controls names something; empty when closed. -->
  <ul
    id={listId}
    class="mention-list"
    role="listbox"
    tabindex="-1"
    aria-label="People and agents to mention"
    hidden={!open}
    bind:this={listEl}
  >
    {#each options as c, i (c.kind + c.id)}
      <li
        id={`${listId}-${i}`}
        class="mention-option"
        class:active={i === active}
        role="option"
        aria-selected={i === active}
        data-index={i}
      >
        <span class="mention-name">{c.name}</span>
        <span class="mention-kind">{describe(c)}</span>
      </li>
    {/each}
  </ul>
  <p class="visually-hidden" role="status">{said}</p>
</div>

<style>
  .mention-field {
    position: relative;
  }

  textarea {
    font: inherit;
    color: var(--ink);
    background: var(--paper);
    border: 1px solid var(--color-line);
    border-radius: 6px;
    padding: 0.5rem 0.625rem;
    width: 100%;
    resize: vertical;
    box-sizing: border-box;
  }

  textarea:focus-visible {
    outline: 2px solid var(--color-red-deep);
    outline-offset: 1px;
  }

  .mention-list {
    position: absolute;
    z-index: 10;
    left: 0;
    right: 0;
    top: calc(100% + 4px);
    margin: 0;
    padding: 0.25rem;
    list-style: none;
    background: var(--paper);
    border: 1px solid var(--color-line);
    border-radius: 6px;
    box-shadow: 0 8px 24px color-mix(in oklab, var(--ink) 14%, transparent);
    max-height: 16rem;
    overflow-y: auto;
  }

  .mention-option {
    display: flex;
    flex-direction: column;
    padding: 0.375rem 0.5rem;
    border-radius: 4px;
    cursor: pointer;
  }

  .mention-option.active {
    background: var(--color-plinth);
    box-shadow: inset 2px 0 0 var(--color-red-deep);
  }

  .mention-name {
    color: var(--ink);
    font-weight: 500;
  }

  .mention-kind {
    color: var(--color-ink-soft);
    font-size: var(--text-meta);
  }

  .visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
    margin: 0;
  }
</style>
